export const FEA_VERTEX_SHADER = `
    #include <clipping_planes_pars_vertex>
    varying vec3 vWorldPosition;
    varying vec3 vNormal;
    varying vec3 vTexCoord;
    varying vec3 vUVW;  // New varying for interpolated texture coords

    uniform float dRmin;
    uniform float dRmax;

    uniform float dGmin;
    uniform float dGmax;

    uniform float dBmin;
    uniform float dBmax;

    uniform float dAmin;
    uniform float dAmax;

    uniform vec3 bboxMin;
    uniform vec3 bboxMax;

    // One voxel in UVW units per axis (1/grid dim) and how many voxels to inset the
    // colour-sampling clamp by, so boundary fragments read the interior value.
    uniform vec3 texelSize;
    uniform float sampleInset;

    uniform sampler3D texture3D;

    uniform vec3 lightDirection;
    uniform vec3 lightColor;
    uniform vec3 ambientColor;
    uniform float uTime;
    uniform int animate;
    uniform int deformationType;
    uniform int vibration;
    uniform float deformScale;

    float scaleValue(float inMin, float inMax, float outMin, float outMax, float value) {
        return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
    }

    void main() {
        #include <begin_vertex>
        #include <project_vertex>
        #include <clipping_planes_vertex>

        // Calculate UVW in vertex shader. Inset the clamp by ~sampleInset voxels per
        // axis so surface fragments at the domain boundary sample the valid interior
        // value instead of the zero-valued clamped/void edge voxel (removes the hard
        // blue edge line). Per-axis because the grid is anisotropic; capped at 0.45 so
        // a thin (few-voxel) axis just samples its mid-plane instead of inverting.
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vec3 vwp = wp.xyz;
        vec3 uvwInset = min(texelSize * sampleInset, vec3(0.45));
        vUVW = clamp((vwp - bboxMin) / (bboxMax - bboxMin), uvwInset, 1.0 - uvwInset);

        if(animate > 0) {
            vec3 bboxSize = bboxMax - bboxMin;
            vec3 normalizedPos = (position - bboxMin) / bboxSize;
            float bboxLen = length(bboxSize) * 0.03 * deformScale;
            float diagonal = length(vec3(dRmax - dRmin, dGmax - dGmin, dBmax - dBmin));

            float dX = dRmin + (texture(texture3D, normalizedPos).r * (dRmax - dRmin));
            float dY = dGmin + (texture(texture3D, normalizedPos).g * (dGmax - dGmin));
            float dZ = dBmin + (texture(texture3D, normalizedPos).b * (dBmax - dBmin));

            vec3 displacement;
            if(vibration == 1) {
                displacement = vec3(dX, dY, dZ) / diagonal * bboxLen * sin(uTime);
            } else {
                displacement = vec3(dX, dY, dZ) / diagonal * bboxLen * (1.0 + sin(uTime) * 0.5);
            }

            vec4 displacedWorldPosition = vec4(position + displacement, 1.0);

            vec4 worldPosition = modelMatrix * displacedWorldPosition;
            vWorldPosition = worldPosition.xyz;
            vNormal = normalize(normalMatrix * normal);
            vTexCoord = normalizedPos;

            gl_Position = projectionMatrix * modelViewMatrix * displacedWorldPosition;
        } else {
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            vNormal = normalize(normalMatrix * normal);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    }
`;

export const FEA_FRAGMENT_SHADER = `
        precision highp float;
        #include <clipping_planes_pars_fragment>

        precision highp sampler3D;

        uniform sampler3D texture3D;
        uniform sampler3D texture3DStress;

        uniform sampler2D colormap;
        uniform vec3 lightDirection;
        uniform vec3 ambientColor;

        varying vec3 vWorldPosition;
        varying vec3 vNormal;
        varying vec3 vTexCoord;

        uniform vec3 diffuseColor;
        uniform vec3 specularColor;
        uniform float shininess;

        uniform int deformationType;

        // LUT range uniforms for custom min/max visualization
        uniform float lutMin;
        uniform float lutMax;

        varying vec3 vUVW;  // Now using interpolated coords

        void main() {
            #include <clipping_planes_fragment>

            // vUVW is already inset away from the domain boundary in the vertex shader,
            // so surface fragments read the valid interior value rather than the
            // zero-valued clamped/void edge voxel.
            vec3 uv = vUVW;
            float scalar;
            if(deformationType == 4) {
                scalar = texture(texture3DStress, uv).r;
            } else if(deformationType == 3) {
                scalar = texture(texture3D, uv).b;
            } else if(deformationType == 2) {
                scalar = texture(texture3D, uv).g;
            } else if(deformationType == 1) {
                scalar = texture(texture3D, uv).r;
            } else if(deformationType == 0) {
                scalar = texture(texture3D, uv).a;
            }

            // Remap scalar to custom LUT range (lutMin to lutMax maps to 0-1)
            float remappedScalar = (scalar - lutMin) / (lutMax - lutMin);
            vec3 color = texture(colormap, vec2(clamp(remappedScalar,0.0,1.0), 0.5)).rgb;

            vec3 N = normalize(vNormal);
            vec3 L = normalize(lightDirection);
            vec3 V = normalize(cameraPosition - vWorldPosition);
            vec3 R = reflect(-L, N);

            float diffuseIntensity = max(dot(N, L), 0.0);
            diffuseIntensity = diffuseIntensity * 0.5 + 0.5;

            vec3 ambient = ambientColor * color;
            vec3 diffuse = diffuseColor * color * diffuseIntensity;
            vec3 specular = specularColor * pow(max(dot(R, V), 0.0), shininess);

            vec3 finalColor = ambient + diffuse + specular;
            gl_FragColor = vec4(finalColor, 1.0);
            gl_FragColor.rgb = pow(gl_FragColor.rgb, vec3(0.5));
        }
    `;
